import {
  EMPTY_ARRAY,
  MarketTypeEnum,
  useCallbackRef,
  useMarketAccountsContext,
  useMarketsContext,
  type Security,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { useCallback, useMemo } from 'react';
import { useFavoriteAggregations } from '../../providers/FavoriteAggregationsContext';
import { useMarketSelectorBuyingPowerEnrichment } from '../MarketSelector/enrichments/useMarketSelectorBuyingPowerEnrichment';
import { useUnsettledEnrichment } from '../MarketSelector/enrichments/useUnsettledEnrichment';
import { useMarketDataMarketSelectorItems } from '../MarketSelector/MarketData/useMarketDataMarketSelectorItems';
import { useMarketSelectorAggregationPresets } from '../MarketSelector/presets/useMarketSelectorAggregationPresets';
import type { MarketSelectorValue } from '../MarketSelector/types';
import { useMarketSelector, type UseMarketSelectorParams } from '../MarketSelector/useMarketSelector';
import { marketSelectorItemsToOldItems } from '../MarketSelector/utils/items';
import { useAllowMultipleAcctsPerCred } from '../MarketSelector/utils/useAllowMultipleAcctsPerCred';

export interface PersistMarketDataCardMarketSelection {
  selectedAggregation: string | undefined;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
}

interface UseMarketDataCardMarketSelectorParams {
  security: Security | undefined;
  ulEnabled: boolean;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  selectedAggregation: string | undefined;
  onPersistMarketSelection: (update: PersistMarketDataCardMarketSelection) => void;
  /** When false, skips expensive per-symbol selector queries until the user opens the selector. */
  buildMarketSelectorInventory?: boolean;
}

/**
 * Market Selector API for the market data card's quick-access panel.
 * Uses the card's current saved config and persists changes directly.
 */
export function useMarketDataCardMarketSelector({
  security,
  ulEnabled,
  selectedMarkets,
  selectedMarketAccounts,
  selectedAggregation,
  onPersistMarketSelection,
  buildMarketSelectorInventory = true,
}: UseMarketDataCardMarketSelectorParams) {
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();
  const { favoriteAggregationsSet } = useFavoriteAggregations();
  const { getOnlyDarkOrNoDarkSelections, isLatestSelectionDark } = useMarketSelectorDarkSelectionGuards();

  const marketSelectorItems = useMarketDataMarketSelectorItems({
    symbol: security?.Symbol,
    includeUnifiedLiquidityMarkets: ulEnabled,
    buildItems: true,
    queryMSSRecords: buildMarketSelectorInventory,
  });

  const oldMarketSelectorItems = useMemo(() => {
    return marketSelectorItemsToOldItems({ items: marketSelectorItems, includeInactiveMarketAccounts: true });
  }, [marketSelectorItems]);

  const newMarketSelectorValue: MarketSelectorValue = useMemo(() => {
    const selections = [...selectedMarkets, ...selectedMarketAccounts];
    return selectedAggregation
      ? { type: 'preset', presetID: selectedAggregation, selections }
      : { type: 'custom', selections };
  }, [selectedMarketAccounts, selectedMarkets, selectedAggregation]);

  const { aggregationPresets, aggregationPresetsByID } = useMarketSelectorAggregationPresets();
  const selectedAggregationLabel =
    selectedAggregation == null ? undefined : aggregationPresetsByID?.get(selectedAggregation)?.label;

  const handleNewMarketSelectorChange = useCallbackRef((value: MarketSelectorValue) => {
    const selections = value.selections;
    const markets = selections.filter(mktOrAccName => marketsByName.has(mktOrAccName));
    const marketAccounts = selections.filter(mktOrAccName => marketAccountsByName.has(mktOrAccName));

    const allDarkOrNoDarkNewSelections = getOnlyDarkOrNoDarkSelections(isLatestSelectionDark(selections), {
      markets,
      marketAccounts,
    });

    onPersistMarketSelection({
      selectedAggregation: value.type === 'preset' ? value.presetID : undefined,
      selectedMarkets: allDarkOrNoDarkNewSelections.markets,
      selectedMarketAccounts: allDarkOrNoDarkNewSelections.marketAccounts,
    });
  });

  const enrichmentsEnabled = buildMarketSelectorInventory;
  const enrichmentSecurity = enrichmentsEnabled ? security : undefined;
  const enrichmentItems = enrichmentsEnabled ? marketSelectorItems : EMPTY_ARRAY;

  const unsettledEnrichment = useUnsettledEnrichment({
    security: enrichmentSecurity,
    marketSelectorItems: enrichmentItems,
  });

  const buyingPowerEnrichment = useMarketSelectorBuyingPowerEnrichment({
    symbol: enrichmentsEnabled ? (security?.Symbol ?? '') : '',
    currency: enrichmentsEnabled ? (security?.BaseCurrency ?? '') : '',
    threshold: '10s',
    quantity: undefined,
    tag: 'MarketDataCardQuickMarketSelector',
    side: undefined,
    simpleModeVisibleColIDs: EMPTY_ARRAY,
  });

  const enrichmentSpecs = useMemo(
    () => compact([unsettledEnrichment, buyingPowerEnrichment]),
    [unsettledEnrichment, buyingPowerEnrichment]
  );

  const allowMultipleAcctsPerCred = useAllowMultipleAcctsPerCred({
    symbol: security?.Symbol,
    ulEnabled,
  });
  const marketSelectorOptions: UseMarketSelectorParams['options'] = useMemo(
    () => ({ allowMultipleAcctsPerCred }),
    [allowMultipleAcctsPerCred]
  );

  const marketSelector = useMarketSelector({
    value: newMarketSelectorValue,
    onChange: handleNewMarketSelectorChange,
    items: marketSelectorItems,
    presets: aggregationPresets,
    enrichmentSpecs,
    options: marketSelectorOptions,
  });
  const favoriteAggregationPresets = useMemo(() => {
    return (marketSelector.presets ?? [])
      .filter(preset => favoriteAggregationsSet.has(preset.id))
      .toSorted((a, b) => a.label.localeCompare(b.label));
  }, [favoriteAggregationsSet, marketSelector.presets]);

  return {
    marketSelector,
    oldMarketSelectorItems,
    marketSelectorItems,
    selectedMarketOrAccountNames: newMarketSelectorValue.selections,
    selectedAggregationID: selectedAggregation,
    selectedAggregationLabel,
    favoriteAggregationPresets,
    selectAggregationPreset: marketSelector.selectPreset,
  };
}

function useMarketSelectorDarkSelectionGuards() {
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName } = useMarketAccountsContext();

  const isMarketDark = useCallback(
    (marketName: string | undefined) => {
      const market = marketName ? marketsByName.get(marketName) : undefined;
      return market ? market.Type === MarketTypeEnum.Dark : false;
    },
    [marketsByName]
  );

  const isMarketAccountDark = useCallback(
    (marketAccountName: string | undefined) => {
      const market = marketAccountName ? marketAccountsByName.get(marketAccountName)?.Market : undefined;
      return market ? isMarketDark(market) : false;
    },
    [marketAccountsByName, isMarketDark]
  );

  const getOnlyDarkOrNoDarkSelections = useCallback(
    (latestSelectionIsDark: boolean, selections: { markets: string[]; marketAccounts: string[] }) => {
      return {
        marketAccounts: selections.marketAccounts.filter(ma =>
          latestSelectionIsDark ? isMarketAccountDark(ma) : !isMarketAccountDark(ma)
        ),
        markets: selections.markets.filter(m => (latestSelectionIsDark ? isMarketDark(m) : !isMarketDark(m))),
      };
    },
    [isMarketAccountDark, isMarketDark]
  );

  const isLatestSelectionDark = useCallback(
    (selections: string[]) => {
      const latestSelection = selections.at(-1);
      if (!latestSelection) {
        return false;
      }

      const isMarketName = marketsByName.has(latestSelection);
      return isMarketName ? isMarketDark(latestSelection) : isMarketAccountDark(latestSelection);
    },
    [marketsByName, isMarketDark, isMarketAccountDark]
  );

  return {
    isMarketDark,
    isMarketAccountDark,
    getOnlyDarkOrNoDarkSelections,
    isLatestSelectionDark,
  };
}
